import React, { useState, useEffect, useRef } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  ArcElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import UsersSection from './UsersSection';
import { API } from '../api';
import './UsersTab.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  ArcElement,
  Tooltip,
  Legend,
  Filler
);

const UsersTab = ({
  users,
  pagination,
  stats,
  loading,
  currency,
  onUserClick,
  onAddHours,
  onViewPayments,
  onPageChange
}) => {
  const [chartData, setChartData] = useState(null);
  const lineChartRef = useRef(null);
  const lineChartInstanceRef = useRef(null);
  const chartColorsRef = useRef({ stroke: 'rgba(255,255,255,0.92)', fill: 'rgba(255,255,255,0.06)', pointBg: '#0a0a0a' });

  const mau = stats && stats.users && stats.users.ma !== undefined ? stats.users.ma : 0;
  const dau = stats && stats.users && stats.users.da !== undefined ? stats.users.da : 0;
  const onlineUsers = stats && stats.users && stats.users.online !== undefined ? stats.users.online : 0;
  const totalUsersStats = stats && stats.users && stats.users.total !== undefined ? stats.users.total : (pagination ? pagination.total : users.length);
  const viewsToday = stats && stats.summaries && stats.summaries.views && stats.summaries.views.today !== undefined ? stats.summaries.views.today : 0;
  
  useEffect(() => {
    const loadChartData = async () => {
      const result = await API.stats.getUsersChart();
      if (result.success) {
        setChartData(result.data);
      }
    };
    loadChartData();
  }, []);

  useEffect(() => {
    try {
      const styles = getComputedStyle(document.documentElement);
      const textPrimary = (styles.getPropertyValue('--text-primary') || '').trim() || 'rgba(255,255,255,0.92)';
      const bgPrimary = (styles.getPropertyValue('--bg-primary') || '').trim() || '#0a0a0a';
      chartColorsRef.current = {
        stroke: textPrimary,
        fill: 'rgba(255,255,255,0.06)',
        pointBg: bgPrimary
      };
    } catch (e) {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (!lineChartRef.current || !chartData) return;

    const ctx = lineChartRef.current.getContext('2d');

    if (lineChartInstanceRef.current) {
      lineChartInstanceRef.current.destroy();
    }

    const data = chartData.values || [];
    const labels = chartData.labels || [];
    const colors = chartColorsRef.current;

    lineChartInstanceRef.current = new ChartJS(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: [
          {
            label: 'Новые пользователи',
            data: data,
            borderColor: colors.stroke,
            backgroundColor: colors.fill,
            borderWidth: 2,
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: colors.pointBg,
            pointBorderColor: colors.stroke,
            pointBorderWidth: 2,
            pointHoverRadius: 6
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        interaction: {
          intersect: false,
          mode: 'index',
        },
        plugins: {
          legend: {
            display: false,
          },
          tooltip: {
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            padding: 12,
            titleFont: { size: 13, weight: 'bold' },
            bodyFont: { size: 12 },
            borderColor: '#e5e7eb',
            borderWidth: 1,
            titleColor: '#ffffff',
            bodyColor: '#ffffff',
            displayColors: false,
            callbacks: {
              label: (context) => {
                return `${context.parsed.y} пользователей`;
              }
            },
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            grace: '10%',
            grid: { color: 'rgba(255,255,255,0.08)', drawBorder: true },
            ticks: { 
              color: 'rgba(255,255,255,0.55)', 
              font: { size: 12 },
              precision: 0
            }
          },
          x: {
            grid: { display: false },
            ticks: { color: 'rgba(255,255,255,0.55)', font: { size: 12 } }
          }
        }
      }
    });

    return () => {
      if (lineChartInstanceRef.current) {
        lineChartInstanceRef.current.destroy();
        lineChartInstanceRef.current = null;
      }
    };
  }, [chartData]);

  return (
    <div className="tab-content">
      {/* Stats Grid */}
      <div className="users-stats-grid">
        <div className="stat-card">
          <div className="stat-label">Всего пользователей</div>
          <div className="stat-value">{totalUsersStats.toLocaleString('ru-RU')}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">MAU</div>
          <div className="stat-value">{mau.toLocaleString('ru-RU')}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">DAU</div>
          <div className="stat-value">{dau.toLocaleString('ru-RU')}</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Онлайн сейчас</div>
          <div className="stat-value">{onlineUsers.toLocaleString('ru-RU')}</div>
          <div className="stat-subtext">Активные сессии</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Просмотры сегодня</div>
          <div className="stat-value">{viewsToday.toLocaleString('ru-RU')}</div>
          <div className="stat-subtext">Саммари просмотрено</div>
        </div>
      </div>

      {/* Chart */}
      <div className="chart-container">
        <div className="chart-card">
          <h3>Прирост пользователей за неделю</h3>
          {chartData ? (
            <canvas ref={lineChartRef}></canvas>
          ) : (
            <div className="empty-chart-message">Нет данных для отображения графика</div>
          )}
        </div>
      </div>
      {/* Users Table */}
      <UsersSection
        users={users}
        pagination={pagination}
        loading={loading}
        onAddHours={onAddHours}
        onViewPayments={onViewPayments}
        currency={currency}
        onUserNameClick={onUserClick}
        showPagination={true}
        onPageChange={onPageChange}
      />
    </div>
  );
};

export default UsersTab;
